/**
 * Small client reliability rules shared by several screens, kept here so they can be tested
 * without a browser: per-user batch keys, storage writes that report failure, image and auth
 * error categories, a bounded work queue and "latest request wins" generations.
 */
import { ApiError } from './errors.ts';

/** Canonical digest of a selection: order and duplicates don't matter, no prefix collisions. */
export async function selectionDigest(ids: readonly string[]): Promise<string> {
  const canonical = [...new Set(ids)].sort().join('\n');
  const bytes = new TextEncoder().encode(canonical);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
  return Array.from(digest, b => b.toString(16).padStart(2, '0')).join('');
}

/** Storage key for a bulk send: user, workspace and the full selection. */
export async function bulkBatchKey(userId: string, workspaceId: string, orderIds: readonly string[]): Promise<string> {
  return `miraops.bulk.v2.${userId}.${workspaceId}.${await selectionDigest(orderIds)}`;
}
export const BULK_KEY_PREFIX = 'miraops.bulk.';

/** Minimal synchronous key/value storage (localStorage or a test double). */
export interface KeyValue { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void; readonly length: number; key(i: number): string | null }

/**
 * Writes and reads back. `false` means the value is not durable on this device (quota,
 * private mode, blocked storage), so the caller must not act as if it were.
 */
export function durableWrite(store: KeyValue | null, key: string, value: string): boolean {
  if (!store) return false;
  try { store.setItem(key, value); return store.getItem(key) === value; } catch { /* ignored: browser storage blocked: reported as not kept / nothing to remove */  return false; }
}

/**
 * Removes bulk-send records that are not this user's (another person signed in on this
 * browser) and the old unscoped format. Returns how many were removed.
 */
export function purgeForeignBulkRecords(store: KeyValue | null, userId: string): number {
  if (!store) return 0;
  const doomed: string[] = [];
  try {
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (!k || !k.startsWith(BULK_KEY_PREFIX)) continue;
      if (!k.startsWith(`${BULK_KEY_PREFIX}v2.${userId}.`)) doomed.push(k);
    }
    for (const k of doomed) store.removeItem(k);
  } catch { /* ignored: browser storage blocked: reported as not kept / nothing to remove */  return 0; }
  return doomed.length;
}

export interface BulkRecord { batchId: string; packages: Record<string, { requestId: string; payload: string; operationId: string | null }> }
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Schema check for a stored bulk record; anything else is ignored. */
export function isBulkRecord(v: unknown): v is BulkRecord {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  if (typeof r.batchId !== 'string' || !UUID.test(r.batchId) || typeof r.packages !== 'object' || r.packages === null) return false;
  return Object.entries(r.packages as Record<string, unknown>).every(([k, p]) => {
    if (!UUID.test(k) || typeof p !== 'object' || p === null) return false;
    const q = p as Record<string, unknown>;
    return typeof q.requestId === 'string' && UUID.test(q.requestId) && typeof q.payload === 'string' && (q.operationId === null || (typeof q.operationId === 'string' && UUID.test(q.operationId)));
  });
}

/** Why a protected image did not load. Only 401/403 mean "not allowed"; a 410 with the code
 *  image_removed means the auction took the photo down (its copy is gone too). */
export type ImageFailure = 'denied' | 'expired' | 'removed' | 'rate_limited' | 'unavailable';
export function imageFailure(status: number | null, code?: string | null): ImageFailure {
  if (status === 401 || status === 403) return 'denied';
  if (status === 410 && code === 'image_removed') return 'removed';
  if (status === 404 || status === 410) return 'expired';
  if (status === 429) return 'rate_limited';
  return 'unavailable';
}
export const retryableImage = (f: ImageFailure) => f === 'rate_limited' || f === 'unavailable';

/**
 * Whether an Auth check at start-up proves the stored session is no longer valid. Only an
 * explicit rejection does; throttling, server errors and no answer keep the session.
 */
export function authRejectsSession(status: number | undefined | null): boolean {
  return status === 401 || status === 403 || status === 404;
}

/** Outcome of an invitation acceptance attempt, so the screen can offer the right next step. */
export type InviteFailure = 'invalid' | 'other_email' | 'retry';
export function inviteFailure(error: unknown): InviteFailure {
  if (!(error instanceof ApiError)) return 'retry';
  if (error.token === 'invitation_other_email') return 'other_email';
  if (error.token === 'invitation_invalid') return 'invalid';
  if (error.status === 0 || error.status === 429 || error.status >= 500) return 'retry';
  return 'invalid';
}

/** Runs tasks with at most `limit` in flight, in the given order. Stops starting new ones once aborted. */
export async function mapLimit<T, R>(items: readonly T[], limit: number, run: (item: T, index: number) => Promise<R>, signal?: AbortSignal): Promise<PromiseSettledResult<R>[]> {
  const out: PromiseSettledResult<R>[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      if (signal?.aborted) return;
      const i = next++;
      try { out[i] = { status: 'fulfilled', value: await run(items[i]!, i) }; } catch (reason) { out[i] = { status: 'rejected', reason }; }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return out;
}

/** Image load order for a gallery: the shown image, then its neighbours outward, then the rest. */
export function galleryLoadOrder(count: number, index: number): number[] {
  const out: number[] = [];
  if (count <= 0) return out;
  const start = Math.max(0, Math.min(count - 1, index));
  out.push(start);
  for (let d = 1; out.length < count; d++) {
    if (start + d < count) out.push(start + d);
    if (start - d >= 0) out.push(start - d);
  }
  return out;
}

/** "Latest request wins": each `next()` invalidates every earlier ticket. */
export class Generation {
  #n = 0;
  next(): number { return ++this.#n; }
  current(ticket: number): boolean { return ticket === this.#n; }
}

/** Arrow keys belong to a text field when one has focus; galleries must not take them. */
export function isEditableTarget(target: { tagName?: string; isContentEditable?: boolean; getAttribute?: (n: string) => string | null } | null): boolean {
  if (!target) return false;
  const tag = (target.tagName ?? '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) return true;
  const role = target.getAttribute?.('role');
  return role === 'textbox' || role === 'combobox' || role === 'listbox' || role === 'option' || role === 'slider' || role === 'spinbutton';
}

/** Roving focus for tabs, radio groups and option lists. Returns the new index or null. */
export function rovingIndex(key: string, current: number, disabled: readonly boolean[], orientation: 'horizontal' | 'vertical' | 'both' = 'both'): number | null {
  const n = disabled.length;
  if (n === 0) return null;
  const forward = key === 'ArrowDown' && orientation !== 'horizontal' || key === 'ArrowRight' && orientation !== 'vertical';
  const back = key === 'ArrowUp' && orientation !== 'horizontal' || key === 'ArrowLeft' && orientation !== 'vertical';
  const scan = (from: number, step: number) => { for (let k = 0, i = from; k < n; k++, i = (i + step + n) % n) if (!disabled[i]) return i; return null; };
  if (key === 'Home') return scan(0, 1);
  if (key === 'End') return scan(n - 1, -1);
  if (forward) return scan((current + 1) % n, 1);
  if (back) return scan((current - 1 + n) % n, -1);
  return null;
}

/** Typeahead: the next option after `current` whose label starts with the typed text. */
export function typeaheadIndex(labels: readonly string[], typed: string, current: number, disabled: readonly boolean[] = []): number | null {
  const q = typed.toLocaleLowerCase();
  if (!q) return null;
  // A longer typed prefix may still match the current option; a single key moves on.
  for (let k = q.length > 1 ? 0 : 1; k <= labels.length; k++) {
    const i = ((current < 0 ? -1 : current) + k + labels.length) % labels.length;
    if (!disabled[i] && labels[i]!.toLocaleLowerCase().startsWith(q)) return i;
  }
  return null;
}
